import { useState, useRef, useEffect, useMemo } from 'react';
import { agentOptions, type AgentOption } from '../config/agentOptions';

/** Gear icon SVG. */
function GearIcon({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}

// Show the filter box only once the list is long enough to be worth searching.
const SEARCH_THRESHOLD = 8;

export function AgentSelector({
  selectedAgent,
  onAgentChange,
}: {
  selectedAgent: AgentOption;
  onAgentChange: (agent: AgentOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  const showSearch = agentOptions.length >= SEARCH_THRESHOLD;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agentOptions;
    return agentOptions.filter(
      (a) =>
        a.label.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q),
    );
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // On open: reset the filter, focus the search box, and bring the active agent into view.
  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    if (showSearch) searchRef.current?.focus();
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [open, showSearch]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setOpen(!open)}
        className="hover-header-btn"
        style={styles.gearButton}
        aria-label="Select agent"
        title={`Agent: ${selectedAgent.label}`}
      >
        <GearIcon size={16} color="white" />
      </button>

      {open && (
        <div
          style={styles.dropdown}
          role="listbox"
          aria-label="Select agent"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
        >
          <div style={styles.dropdownHeader}>Select Agent</div>

          {showSearch && (
            <div style={styles.searchWrap}>
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search agents…"
                aria-label="Search agents"
                style={styles.searchInput}
              />
            </div>
          )}

          <div style={styles.optionList}>
            {filtered.map((agent) => {
              const isActive = agent.id === selectedAgent.id;
              return (
                <button
                  key={agent.id}
                  ref={isActive ? activeRef : undefined}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    onAgentChange(agent);
                    setOpen(false);
                  }}
                  style={{
                    ...styles.option,
                    background: isActive ? '#e8f0fe' : 'transparent',
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  <div style={styles.optionLabel}>
                    {agent.label}
                    {isActive && <span style={styles.activeBadge}>Active</span>}
                  </div>
                  <div style={styles.optionDesc}>{agent.description}</div>
                </button>
              );
            })}

            {filtered.length === 0 && (
              <div style={styles.noResults}>No agents match “{query.trim()}”.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  gearButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    border: '1px solid rgba(255,255,255,0.4)',
    background: 'rgba(255,255,255,0.15)',
    color: 'white',
    borderRadius: '50%',
    cursor: 'pointer',
  },
  dropdown: {
    position: 'absolute',
    top: 44,
    right: 0,
    background: 'white',
    borderRadius: 8,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    border: '1px solid #e1dfdd',
    minWidth: 280,
    // Never grow past the viewport; the option list scrolls instead of running off-screen.
    maxHeight: 'min(70vh, 480px)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 100,
    overflow: 'hidden',
  },
  dropdownHeader: {
    padding: '12px 16px 8px',
    fontSize: 12,
    fontWeight: 600,
    color: '#616161',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    flexShrink: 0,
  },
  searchWrap: {
    padding: '0 12px 8px',
    flexShrink: 0,
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box' as const,
    padding: '6px 10px',
    fontSize: 13,
    color: '#242424',
    border: '1px solid #d1d1d1',
    borderRadius: 6,
    outline: 'none',
  },
  optionList: {
    // The only scrollable region — keeps header + search pinned while the list scrolls.
    overflowY: 'auto',
    flex: 1,
    paddingBottom: 4,
  },
  option: {
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    padding: '10px 16px',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    color: '#242424',
  },
  optionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  optionDesc: {
    fontSize: 12,
    color: '#616161',
    marginTop: 2,
  },
  activeBadge: {
    fontSize: 10,
    fontWeight: 600,
    background: '#0078d4',
    color: 'white',
    borderRadius: 4,
    padding: '1px 6px',
  },
  noResults: {
    padding: '16px',
    fontSize: 13,
    color: '#616161',
    textAlign: 'center' as const,
  },
};
